/*
  Clinic links and the clinic gift, for the Skinade clinic programme.
  Design: docs/plans/2026-09-25-onboarding-revision-design.md §4.3 and §4.6
  in the skinade-refersion repo.

  - A visitor who lands with a clinic's Refersion token (?rfsn=) gets that
    clinic's discount code applied through Shopify's /discount/ link, once per
    session, the last clinic's link winning (§4.6, D6): the cart's codes are
    set to exactly that clinic's code with /cart/update.js, not by leaving
    the page, and a landing cut short by another script's navigation is
    finished on the next page.
  - While a clinic code is on the cart and a paid line is in it, one unit of
    the programme's gift is added. The discount Function is the authority: a
    gift unit without a discount titled with the clinic code, worth one whole
    unit, is taken away (§4.3, rules 1 to 5). The state is kept in the cart
    attribute _clinic_gift, which the checkout extension reads too, in one
    schema: {"code", "variantId", "revision", "at", "tried"?}.
  - As the checkout extension does: a gift is taken away one unit at a time,
    so paid units merged into its line stay. With two or more codes on the
    cart and no clinic link picking one, only the theme's own gift unit is
    taken away (one unit of the stored state's gift, with the state, unless a
    line of it is marked by any applied code; never for a `tried` state);
    nothing else is changed (final review, 26 Sep, S1).
*/
(function () {
  'use strict';

  var CONFIG = window.ClinicGiftConfig || {};
  var ATTRIBUTE = '_clinic_gift';
  var STATE_KEYS = ['code', 'variantId', 'revision', 'at', 'tried'];
  var LOOKUP_TTL_MS = 5 * 60 * 1000;
  var TIMEOUT_MS = 3000;
  // Task 9's grammar, copied exactly from src/http/lookup.ts in the
  // skinade-refersion repo (plan F3): CODE_PATH's code and TOKEN_PATH's token.
  var CODE = /^[A-Za-z0-9]{3,26}$/;
  var TOKEN = /^[1-9][0-9]{0,11}\.[A-Za-z0-9]{4,32}$/;
  var VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/([1-9][0-9]*)$/;
  var CART_CHANGE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  // `tried` when the theme took its gift away with no other paid line in the
  // cart: F2 allows only a non-empty string, and no signature reads so.
  var NO_PAID_LINES = 'none';

  // ---- The pure core (tested in docs/clinic-gift/clinic-gift.test.mjs) ----

  function variantNumber(gid) {
    var match = VARIANT_GID.exec(gid || '');
    return match ? Number(match[1]) : null;
  }

  // A clinic code as the Worker stores it and F2 writes it: upper case.
  function isCode(value) {
    return typeof value === 'string' && CODE.test(value) && value === value.toUpperCase();
  }

  function isVariantOrNull(value) {
    return value === null || (typeof value === 'string' && VARIANT_GID.test(value));
  }

  function isRevision(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
  }

  // A time exactly as Date.prototype.toISOString writes it.
  function isIsoTime(value) {
    if (typeof value !== 'string') {
      return false;
    }
    var ms = Date.parse(value);
    return isFinite(ms) && new Date(ms).toISOString() === value;
  }

  function rawState(cart) {
    var raw = cart && cart.attributes ? cart.attributes[ATTRIBUTE] : null;
    return typeof raw === 'string' && raw !== '' ? raw : null;
  }

  function parsedObject(raw) {
    var value;
    try {
      value = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  }

  // F2: {"code", "variantId", "revision", "at", "tried"?}. Anything else (bad
  // JSON, a missing or wrongly typed field, an unknown field) is absent, and
  // plan() clears it. In memory `at` is a number and a missing `tried` null.
  function readState(cart) {
    var raw = rawState(cart);
    var stored = raw === null ? null : parsedObject(raw);
    if (stored === null) {
      return null;
    }
    var keys = Object.keys(stored);
    for (var i = 0; i < keys.length; i += 1) {
      if (STATE_KEYS.indexOf(keys[i]) === -1) {
        return null;
      }
    }
    if (!isCode(stored.code) || !('variantId' in stored) || !isVariantOrNull(stored.variantId)) {
      return null;
    }
    if (!isRevision(stored.revision) || !isIsoTime(stored.at)) {
      return null;
    }
    if ('tried' in stored && (typeof stored.tried !== 'string' || stored.tried === '')) {
      return null;
    }
    return {
      code: stored.code,
      variantId: stored.variantId,
      revision: stored.revision,
      at: Date.parse(stored.at),
      tried: 'tried' in stored ? stored.tried : null
    };
  }

  // The gift a refused state names: valid JSON with a well-formed variantId
  // is enough, so its unit can still be taken away (Task 13's looseGiftVariant).
  function looseVariant(cart) {
    var raw = rawState(cart);
    var stored = raw === null ? null : parsedObject(raw);
    return stored && typeof stored.variantId === 'string' && VARIANT_GID.test(stored.variantId) ? stored.variantId : null;
  }

  // `tried` is written only when it is a non-empty string (F2).
  function serialiseState(state) {
    var out = {
      code: state.code,
      variantId: state.variantId,
      revision: state.revision,
      at: new Date(state.at).toISOString()
    };
    if (typeof state.tried === 'string' && state.tried !== '') {
      out.tried = state.tried;
    }
    return JSON.stringify(out);
  }

  function withTried(state, tried) {
    return { code: state.code, variantId: state.variantId, revision: state.revision, at: state.at, tried: tried };
  }

  // The /c/ answer as the state needs it (ruling T7). `found` is false for a
  // 404, null when the Worker could not be reached, else the parsed body.
  // null back means "unreachable" (rule 5).
  function answerOf(found) {
    if (found === false) {
      return { variantId: null, revision: 0 };
    }
    if (!found || typeof found !== 'object' || !isVariantOrNull(found.giftVariantId) || !isRevision(found.revision)) {
      return null;
    }
    return { variantId: found.giftVariantId, revision: found.revision };
  }

  // Each code once, as the checkout extension counts them (D6).
  function cartCodes(cart) {
    return (cart.discount_codes || [])
      .map(function (entry) { return String((entry && entry.code) || '').trim().toUpperCase(); })
      .filter(function (code, index, all) { return code !== '' && all.indexOf(code) === index; });
  }

  // Go/no-go D6, the last click: the /cart/update.js `discount` body that
  // leaves exactly the landed clinic's code on the cart, or null when that
  // is already so (or there is no landed code).
  function linkDiscount(cart, landed) {
    var codes = cartCodes(cart);
    return isCode(landed) && !(codes.length === 1 && codes[0] === landed) ? landed : null;
  }

  // The clinic code to act on. One code: it, when it is in Task 9's grammar
  // (ruling T8), else null, which is "no clinic code" (rule 1). Two or more:
  // the one this session's clinic link applied (ruling T6, the last click),
  // else false, which is "hands off" but for the theme's own gift unit
  // (plan, S1), as at checkout.
  function pickCode(cart, linkCode) {
    var codes = cartCodes(cart);
    if (codes.length > 1) {
      return isCode(linkCode) && codes.indexOf(linkCode) !== -1 ? linkCode : false;
    }
    return codes.length === 1 && isCode(codes[0]) ? codes[0] : null;
  }

  function linesOf(cart, gid) {
    var number = variantNumber(gid);
    return number === null ? [] : (cart.items || []).filter(function (item) { return item.variant_id === number; });
  }

  // Ruling T3 (G1, F1): the Function's marker is an allocation titled with
  // the applied code, in any case, worth exactly one unit of the line. Paid
  // units merged into the line leave it marked, as the checkout reads it.
  function isMarked(line, code) {
    if (!(line.quantity >= 1)) {
      return false;
    }
    var unit = line.original_line_price / line.quantity;
    var wanted = String(code).trim().toUpperCase();
    return unit > 0 && (line.line_level_discount_allocations || []).some(function (allocation) {
      return (
        allocation &&
        allocation.discount_application &&
        typeof allocation.discount_application.title === 'string' &&
        allocation.discount_application.title.trim().toUpperCase() === wanted &&
        Math.abs(allocation.amount - unit) < 0.5
      );
    });
  }

  // The paid lines other than the gift, as "<variant>x<quantity>", sorted.
  function paidSignature(cart, gid) {
    var number = variantNumber(gid);
    return (cart.items || [])
      .filter(function (item) { return item.variant_id !== number && item.final_line_price > 0; })
      .map(function (item) { return item.variant_id + 'x' + item.quantity; })
      .sort()
      .join(',');
  }

  function needsLookup(cart, state, linkCode, now) {
    var code = pickCode(cart, linkCode);
    if (typeof code !== 'string') {
      return null;
    }
    return state && state.code === code && now - state.at < LOOKUP_TTL_MS ? null : code;
  }

  // A line's quantity once the planned removals are made.
  function plannedQuantity(actions, line) {
    for (var i = 0; i < actions.remove.length; i += 1) {
      if (actions.remove[i].key === line.key) {
        return actions.remove[i].quantity;
      }
    }
    return line.quantity;
  }

  function setQuantity(actions, line, quantity) {
    for (var i = 0; i < actions.remove.length; i += 1) {
      if (actions.remove[i].key === line.key) {
        actions.remove[i].quantity = quantity;
        return;
      }
    }
    actions.remove.push({ key: line.key, quantity: quantity });
  }

  // Ruling 1: a gift is taken away one unit at a time; the rest of a merged
  // line is paid. Not when a line of that variant carries `keepFor`'s marker
  // (the applied code's gift, ruling 2). True when a unit was taken.
  function takeOneUnit(actions, cart, gid, keepFor) {
    var lines = linesOf(cart, gid).filter(function (item) { return plannedQuantity(actions, item) > 0; });
    if (lines.length === 0) {
      return false;
    }
    if (keepFor && lines.some(function (item) { return isMarked(item, keepFor); })) {
      return false;
    }
    setQuantity(actions, lines[0], plannedQuantity(actions, lines[0]) - 1);
    return true;
  }

  // Rules 1, 2, 4 and 5 (§4.3). `answer`: undefined when no lookup was made,
  // null when the Worker could not be reached, else {variantId, revision}
  // (answerOf). Returns:
  //   remove   [{key, quantity}]: lines set to a lower quantity (0 removes);
  //   add      the gift gid to add one unit of, or null;
  //   addLine  {key, quantity} when that unit joins a line already there;
  //   state    undefined (unchanged), null (clear) or the new state;
  //   unowned  true when the gift variant's lines are the customer's, not
  //            the theme's (verify() then leaves them).
  // The theme's gift unit is taken to be in the cart while the state names
  // it with no `tried`; `tried` says the theme took it away (or it never
  // stayed) for those paid lines.
  function plan(cart, state, linkCode, answer, now) {
    var actions = { remove: [], add: null, addLine: null, state: undefined, unowned: false };
    var code = pickCode(cart, linkCode);
    if (code === false) {
      // Final review, 26 Sep (S1; the open two-code decision, G1): the gift
      // unit the theme added under a stored state not marked tried would
      // otherwise stay at full price. One unit of it goes, with the state so
      // no second is taken, unless a line of it carries any applied code's
      // marker. A tried state, or none, changes nothing.
      if (state && state.tried === null && state.variantId !== null) {
        var codes = cartCodes(cart);
        var gift = linesOf(cart, state.variantId);
        var kept = gift.some(function (item) {
          return codes.some(function (one) { return isMarked(item, one); });
        });
        if (!kept && takeOneUnit(actions, cart, state.variantId, null)) {
          actions.state = null;
        }
      }
      return actions;
    }
    if (state === null && rawState(cart) !== null) {
      var loose = looseVariant(cart);
      if (loose !== null) {
        takeOneUnit(actions, cart, loose, code);
      }
      actions.state = null;
    }
    var held = state && state.variantId !== null && state.tried === null;
    if (code === null) {
      if (held) {
        takeOneUnit(actions, cart, state.variantId, null);
      }
      if (state) {
        actions.state = null;
      }
      return actions;
    }
    var current = state && state.code === code ? state : null;
    if (answer === null) {
      // Rule 5. Another code's gift goes, once: the state goes with it.
      if (state && current === null && held && takeOneUnit(actions, cart, state.variantId, code)) {
        actions.state = null;
      }
      return actions;
    }
    var carried = current !== null && current.tried === null;
    if (answer !== undefined) {
      var sameVariant = state !== null && state.variantId === answer.variantId;
      var next = {
        code: code,
        variantId: answer.variantId,
        revision: answer.revision,
        at: now,
        tried: current && sameVariant ? current.tried : null
      };
      if (held && !sameVariant) {
        // Rule 2: the old gift goes before the new one comes. For a new code,
        // not a unit the Function marks for it.
        takeOneUnit(actions, cart, state.variantId, current ? null : code);
      }
      carried = Boolean(held && sameVariant);
      actions.state = next;
      current = next;
    }
    if (current === null || current.variantId === null) {
      return actions;
    }
    var all = linesOf(cart, current.variantId);
    var lines = all.filter(function (item) { return plannedQuantity(actions, item) > 0; });
    var marked = lines.filter(function (item) { return isMarked(item, code); });
    if (lines.length > 1) {
      // Rule 4: never more than one gift line; the surplus goes whole.
      var keep = marked.length > 0 ? marked[0] : lines[lines.length - 1];
      lines.forEach(function (item) {
        if (item !== keep) {
          setQuantity(actions, item, 0);
        }
      });
      lines = [keep];
    }
    if (marked.length > 0) {
      if (current.tried !== null) {
        actions.state = withTried(current, null);
      }
      return actions;
    }
    if (lines.length > 0 && carried) {
      // The theme's own gift unit: rule 3 (verify) judges it.
      return actions;
    }
    var signature = paidSignature(cart, current.variantId);
    if (signature !== '' && signature !== current.tried) {
      actions.add = current.variantId;
      var into = lines.length > 0 ? lines[0] : all[0];
      if (into) {
        actions.addLine = { key: into.key, quantity: plannedQuantity(actions, into) + 1 };
      }
      // Written with the add (ruling 4): from now the unit is the theme's.
      actions.state = withTried(current, null);
    } else if (lines.length > 0) {
      actions.unowned = true;
    }
    return actions;
  }

  // Rule 3, after the changes: an unmarked gift unit goes, and so does an
  // attempt that did not stay; the paid lines are remembered in `tried` so it
  // is not tried again until they change (ruling T1). Units the theme did not
  // add (`unowned`, or a `tried` state with no new attempt) are left alone.
  function verify(cart, state, attempted, unowned) {
    var none = { remove: [], state: undefined };
    if (!state || state.variantId === null) {
      return none;
    }
    var lines = linesOf(cart, state.variantId);
    if (lines.some(function (item) { return isMarked(item, state.code); })) {
      return none;
    }
    var tried = paidSignature(cart, state.variantId) || NO_PAID_LINES;
    if (lines.length === 0) {
      return attempted ? { remove: [], state: withTried(state, tried) } : none;
    }
    if (!attempted && (unowned || state.tried !== null)) {
      return none;
    }
    return { remove: [{ key: lines[0].key, quantity: lines[0].quantity - 1 }], state: withTried(state, tried) };
  }

  // The one /cart/update.js body for a set of actions: line quantities by
  // key, the gift by variant id (added when it is not in the cart) or by the
  // key of the line it joins, and the state beside them (ruling 4).
  function requestBody(actions) {
    var body = {};
    var updates = {};
    var count = 0;
    (actions.remove || []).forEach(function (change) {
      updates[change.key] = change.quantity;
      count += 1;
    });
    if (actions.addLine) {
      updates[actions.addLine.key] = actions.addLine.quantity;
      count += 1;
    } else if (actions.add) {
      updates[String(variantNumber(actions.add))] = 1;
      count += 1;
    }
    if (count > 0) {
      body.updates = updates;
    }
    if (actions.state !== undefined) {
      body.attributes = {};
      body.attributes[ATTRIBUTE] = actions.state === null ? '' : serialiseState(actions.state);
    }
    return body;
  }

  // The cart's lines as sorted "<variant>x<quantity>": a pass that leaves it
  // unchanged (a gift added, then taken back by verify) changes nothing the
  // visitor can see, so it neither reloads nor announces (WCAG 3.2.2).
  function cartFingerprint(cart) {
    return (cart.items || [])
      .map(function (item) { return item.variant_id + 'x' + item.quantity; })
      .sort()
      .join(',');
  }

  // ---- The browser side ----

  function session(key, value) {
    try {
      if (value === undefined) {
        return window.sessionStorage.getItem(key);
      }
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch (error) {
      return null;
    }
    return null;
  }

  // Checked once: link-following and reloads rely on sessionStorage to stop
  // (once per session, at most two reloads), so without it neither happens.
  var storageOk = null;
  function storageWorks() {
    if (storageOk === null) {
      var probe = String(Date.now());
      session('clinicGiftProbe', probe);
      storageOk = session('clinicGiftProbe') === probe;
      session('clinicGiftProbe', null);
    }
    return storageOk;
  }

  async function cartJson() {
    var response = await fetch('/cart.js', { headers: { accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error('/cart.js answered ' + response.status);
    }
    return await response.json();
  }

  async function post(path, body) {
    var response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    });
    if (!response.ok) {
      throw new Error(path + ' answered ' + response.status);
    }
    return await response.json();
  }

  // One try, three seconds (rule 5). null: unreachable or failing (a 429
  // from the Worker's rate limit included); false: 404.
  async function lookup(path) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    try {
      var response = await fetch(CONFIG.workerUrl + path, {
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      if (response.status === 404) {
        return false;
      }
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  async function giftAnswer(code) {
    return answerOf(await lookup('/c/' + encodeURIComponent(code)));
  }

  function hasWork(actions) {
    return actions.remove.length > 0 || actions.add !== null || actions.state !== undefined;
  }

  function giftUnits(cart, state) {
    return state && state.variantId
      ? linesOf(cart, state.variantId).reduce(function (sum, item) { return sum + item.quantity; }, 0)
      : 0;
  }

  var status = { running: false, runs: 0 };
  var again = false;

  // D6: before the gift is looked up, a landed clinic code is made the
  // cart's only code. The marker stays until that holds, so a page left
  // mid-request finishes on the next; with an empty cart it waits for the
  // first line. At most LINK_TRIES requests per landing, so no loop.
  var LINK_TRIES = 2;
  function doneLanding() {
    session('clinicLinkLanded', null);
    session('clinicLinkTries', null);
  }
  async function lastClick(cart) {
    var landed = session('clinicLinkLanded');
    if (landed === null) {
      return cart;
    }
    var discount = linkDiscount(cart, landed);
    if (discount === null) {
      doneLanding();
      return cart;
    }
    if ((cart.items || []).length === 0) {
      return cart;
    }
    var tries = Number(session('clinicLinkTries')) || 0;
    if (tries >= LINK_TRIES) {
      doneLanding();
      return cart;
    }
    session('clinicLinkTries', String(tries + 1));
    try {
      await post('/cart/update.js', { discount: discount });
      cart = await cartJson();
    } catch (error) {
      if (window.console) {
        window.console.warn('clinic gift: ' + (error && error.message));
      }
      return cart;
    }
    if (linkDiscount(cart, landed) === null) {
      doneLanding();
    }
    return cart;
  }

  async function syncOnce(given) {
    var cart = await lastClick(given || (await cartJson()));
    var linkCode = session('clinicLinkCode');
    var code = pickCode(cart, linkCode);
    var shown = cartFingerprint(cart);
    var state = readState(cart);
    var before = giftUnits(cart, state);
    var giftBefore = state ? state.variantId : null;
    var ownGiftBefore = state && state.code === code ? state.variantId : null;
    var now = Date.now();
    var lookupCode = needsLookup(cart, state, linkCode, now);
    var answer = lookupCode === null ? undefined : await giftAnswer(lookupCode);
    // No-gift design D5: a logged-in customer with an earlier order gets no
    // gift, so its answer is taken as no gift (a paused gift's path: the code
    // stays and the state is written, with no line added), and the cart page
    // says why.
    if (CONFIG.returning === true && answer && answer.variantId !== null) {
      answer = { variantId: null, revision: answer.revision };
      session('clinicGiftReturning', lookupCode);
    }
    var first = plan(cart, state, linkCode, answer, now);
    var attempted = false;
    if (hasWork(first)) {
      try {
        await post('/cart/update.js', requestBody(first));
        attempted = first.add !== null;
        if (first.state !== undefined) {
          state = first.state;
        }
      } catch (error) {
        if (first.add === null) {
          throw error;
        }
        // The gift refused (sold out, say): the rest without it, the attempt
        // recorded as tried so it is not asked again for these paid lines.
        var refused = first.state ? withTried(first.state, paidSignature(cart, first.add) || NO_PAID_LINES) : first.state;
        await post('/cart/update.js', requestBody({ remove: first.remove, add: null, addLine: null, state: refused }));
        if (refused !== undefined) {
          state = refused;
        }
      }
      cart = await cartJson();
    }
    var check = state && state.code === code ? verify(cart, state, attempted, first.unowned) : { remove: [], state: undefined };
    if (check.remove.length > 0 || check.state !== undefined) {
      await post('/cart/update.js', requestBody({ remove: check.remove, add: null, addLine: null, state: check.state }));
      if (check.state !== undefined) {
        state = check.state;
      }
      if (check.remove.length > 0) {
        cart = await cartJson();
      }
    }
    // A gift given before the customer logged in, taken away now the
    // Function no longer marks it, is a withheld gift too (Codex review).
    if (CONFIG.returning === true && typeof code === 'string' && ownGiftBefore && before > 0 &&
        giftUnits(cart, { variantId: ownGiftBefore }) < before) {
      session('clinicGiftReturning', code);
    }
    showReturningNotice(code);
    // Only a change the visitor can see is reported (fix round 1, item 1): a
    // gift added and taken back, or a refused add, leaves the lines as shown.
    if (cartFingerprint(cart) !== shown) {
      var giftAfter = state ? state.variantId : null;
      afterChange(cart, before, giftUnits(cart, state), Boolean(giftBefore && giftAfter && giftBefore !== giftAfter));
    }
  }

  // `given`: a cart already read on this page load, for the first pass.
  async function sync(given) {
    if (status.running) {
      again = true;
      return;
    }
    status.running = true;
    try {
      do {
        again = false;
        await syncOnce(given);
        given = null;
      } while (again);
    } catch (error) {
      if (window.console) {
        window.console.warn('clinic gift: ' + (error && error.message));
      }
    } finally {
      status.running = false;
      status.runs += 1;
    }
  }

  // Visually hidden live regions of the script's own (fix round 1, item 3;
  // fix round 2, items 1 and 3). "page" sits on <body>, outside the cart's
  // wrapper (hidden when the cart is empty); "popup" sits inside the open
  // cart popup, a modal dialog, where a screen reader may ignore anything
  // outside it. The snippet loads in <head>, so they are made here. The
  // hiding is a11y.js's own, nowrap included, so words don't run together.
  var HIDDEN_CSS =
    'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;' +
    'clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
  var OPEN_POPUP = '[data-cart-popup-wrapper]:not(.cart-popup-wrapper--hidden)';

  function statusRegion() {
    var popup = document.querySelector(OPEN_POPUP);
    var container = popup || document.body;
    var name = popup ? 'popup' : 'page';
    if (!container) {
      return null;
    }
    var region = container.querySelector('[data-clinic-gift-status="' + name + '"]');
    if (region) {
      return region;
    }
    region = document.createElement('div');
    region.setAttribute('data-clinic-gift-status', name);
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.style.cssText = HIDDEN_CSS;
    container.appendChild(region);
    return region;
  }

  // The region is emptied, then written after a pause: a region just added
  // is in the accessibility tree before its text changes, and the same
  // message twice is heard twice (fix round 2, item 2). The region is chosen
  // again when writing, as the cart popup may have opened meanwhile. `done`
  // runs once the text is written.
  function announce(message, done) {
    var region = statusRegion();
    if (!message || !region) {
      return;
    }
    region.textContent = '';
    setTimeout(function () {
      var target = statusRegion();
      if (target !== region) {
        target.textContent = '';
        setTimeout(function () { write(target); }, 300);
      } else {
        write(target);
      }
    }, 500);
    function write(target) {
      target.textContent = message;
      if (done) {
        done();
      }
    }
  }

  // D5: on the cart page, while the code whose gift was withheld is the
  // cart's code, the cart header's notice (sections/cart-template.liquid,
  // rendered only for a logged-in customer with orders) is filled from its
  // data-clinic-gift-notice text, the locale string
  // cart.general.clinic_gift_returning. It is a role="status" region, so the
  // text is announced once per page.
  function showReturningNotice(code) {
    if (CONFIG.returning !== true || !CONFIG.cartPage || typeof code !== 'string' || session('clinicGiftReturning') !== code) {
      return;
    }
    var slot = document.querySelector('[data-clinic-gift-notice]');
    if (slot && !slot.textContent) {
      slot.textContent = slot.getAttribute('data-clinic-gift-notice') || '';
    }
  }

  // The notice for a change in the gift's units, or a swap of one gift
  // variant for another (fix round 2, item 4); null when there is none.
  function noticeFor(before, after, swapped) {
    if (swapped && before > 0 && after > 0) {
      return 'Your clinic gift has changed.';
    }
    if (after > before) {
      return 'Your free clinic gift has been added to your basket.';
    }
    if (after < before) {
      return 'The free clinic gift has been removed from your basket.';
    }
    return null;
  }

  // Ruling T4: the header count follows at once; the cart page reloads once,
  // with a message for the live region after the reload. Without a working
  // sessionStorage, or with the reload budget spent, or off the cart page,
  // the message is announced at once instead (fix round 1, items 2 and 4).
  function afterChange(cart, before, after, swapped) {
    var counts = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < counts.length; i += 1) {
      counts[i].textContent = String(cart.item_count);
    }
    var bubbles = document.querySelectorAll('[data-cart-count-bubble]');
    for (var j = 0; j < bubbles.length; j += 1) {
      bubbles[j].classList.toggle('hide', cart.item_count === 0);
    }
    var notice = noticeFor(before, after, swapped);
    if (!CONFIG.cartPage || !storageWorks()) {
      announce(notice);
      return;
    }
    var recent = (session('clinicGiftReloads') || '').split(',').map(Number).filter(function (at) {
      return Date.now() - at < 30000;
    });
    if (recent.length >= 2) {
      announce(notice);
      return;
    }
    recent.push(Date.now());
    session('clinicGiftReloads', recent.join(','));
    session('clinicGiftNotice', notice);
    window.location.reload();
  }

  // The message left before a reload; it is cleared only once written.
  function announceNotice() {
    var notice = session('clinicGiftNotice');
    if (notice) {
      announce(notice, function () { session('clinicGiftNotice', null); });
    }
  }

  // §4.6 and D6: a landing with a clinic's rfsn token. The token is read
  // by the snippet's inline script as the page is parsed (another script
  // may strip it from the address before this deferred one runs), and noted
  // at once, before any request, so a page left mid-lookup resolves it on
  // the next. The same token twice in a row is one landing; another
  // clinic's link, or the first again after it, is a new last click. Never
  // without a working sessionStorage (fix round 1, item 4).
  function noteLanding() {
    var token = 'rfsn' in CONFIG ? CONFIG.rfsn : new URLSearchParams(window.location.search).get('rfsn');
    if (typeof token === 'string' && TOKEN.test(token) && storageWorks() && session('clinicLinkToken') !== token) {
      session('clinicLinkPending', token);
    }
  }

  // The pending token's clinic code becomes the link's code and the landed
  // code that lastClick() puts on the cart. Asked once, whatever the answer.
  async function resolveLanding() {
    var token = session('clinicLinkPending');
    if (token === null || !TOKEN.test(token)) {
      return;
    }
    var answer = await lookup('/r/' + encodeURIComponent(token));
    session('clinicLinkPending', null);
    session('clinicLinkToken', token);
    if (answer && isCode(answer.code)) {
      session('clinicLinkCode', answer.code);
      session('clinicLinkLanded', answer.code);
      session('clinicLinkTries', null);
    }
  }

  // Ruling T5.
  function bindCartEvents() {
    var bound = [];
    [window.jQuery, window.$].forEach(function (jq) {
      if (typeof jq !== 'function' || !jq.fn || bound.indexOf(jq) !== -1) {
        return;
      }
      bound.push(jq);
      jq(document).on('ajaxComplete', function (event, xhr, settings) {
        if (settings && CART_CHANGE.test(String(settings.url))) {
          sync();
        }
      });
    });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        sync();
      }
    });
  }

  async function start() {
    noteLanding();
    announceNotice();
    var cart;
    try {
      cart = await cartJson();
    } catch (error) {
      return;
    }
    await resolveLanding();
    bindCartEvents();
    // The cart just read serves the first pass: one /cart.js per page load.
    await sync(cart);
  }

  window.ClinicGift = {
    readState: readState,
    looseVariant: looseVariant,
    serialiseState: serialiseState,
    answerOf: answerOf,
    cartCodes: cartCodes,
    linkDiscount: linkDiscount,
    pickCode: pickCode,
    isMarked: isMarked,
    paidSignature: paidSignature,
    needsLookup: needsLookup,
    plan: plan,
    verify: verify,
    requestBody: requestBody,
    cartFingerprint: cartFingerprint,
    noticeFor: noticeFor,
    announce: announce,
    sync: sync,
    status: status
  };

  if (typeof CONFIG.workerUrl === 'string' && /^https:\/\//.test(CONFIG.workerUrl)) {
    start();
  }
})();
